import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/content";
import { formatDate, formatViews } from "@/lib/content";
import CategoryBadge from "./CategoryBadge";

interface NewsCardProps {
  item: NewsItem;
  variant?: "grid" | "list" | "compact";
  index?: number;
}

export default function NewsCard({ item, variant = "grid", index = 0 }: NewsCardProps) {
  const delay = { animationDelay: `${Math.min(index * 60, 360)}ms` };

  if (variant === "list") {
    return (
      <Link
        to={`/detail/${item.id}`}
        style={delay}
        className="group animate-fade-up flex flex-col sm:flex-row gap-4 md:gap-5 bg-background-50 border border-background-200 rounded-lg p-3 md:p-4 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-52 md:w-60 h-40 sm:h-32 md:h-36 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={item.cover}
            alt={`${item.title} - 小蜜桃配图`}
            title={`${item.title} - 小蜜桃`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryBadge categoryKey={item.categoryKey} name={item.categoryName} />
            <span className="text-xs text-foreground-500">{item.subType}</span>
          </div>
          <h3 className="mt-2 font-heading font-semibold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {item.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 flex-1">
            {item.summary}
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
            <span>{item.author}</span>
            <span>{formatDate(item.publishDate)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(item.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group flex gap-3 items-start cursor-pointer"
      >
        <span className="w-6 h-6 shrink-0 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center text-xs font-semibold">
          {index + 1}
        </span>
        <div className="min-w-0">
          <h4 className="text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {item.title}
          </h4>
          <span className="text-xs text-foreground-500 mt-1 inline-block">
            {item.categoryName} · {formatDate(item.publishDate)}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${item.id}`}
      style={delay}
      className="group animate-fade-up flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="w-full h-48 md:h-52 overflow-hidden bg-background-200">
        <img
          src={item.cover}
          alt={`${item.title} - 小蜜桃配图`}
          title={`${item.title} - 小蜜桃`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 flex flex-col p-4 md:p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <CategoryBadge categoryKey={item.categoryKey} name={item.categoryName} />
          <span className="text-xs text-foreground-500">{item.subType}</span>
        </div>
        <h3 className="mt-3 font-heading font-semibold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
          {item.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 flex-1">
          {item.summary}
        </p>
        <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
          <span>{formatDate(item.publishDate)}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(item.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}